<template>
    <div class="container">
        <!-- Scratch view for trying out markup and behaviour. Reachable at /test,
             not linked from the UI. Keep it free of real customer data: whatever
             is here ends up in the production bundle, which is served without
             authentication even though this route is guarded. -->
        <h4 class="mt-3">NFC test</h4>

        <div v-if="!supported" class="alert alert-warning">
            <p class="mb-2"><strong>This browser does not support Web NFC.</strong></p>
            <p v-if="!secure" class="mb-2">
                Web NFC needs a secure connection — open the app over HTTPS (or localhost).
            </p>
            <p class="mb-1">Browsers that may work:</p>
            <ul class="mb-2">
                <li><strong>Chrome for Android</strong> (version 89 or newer) — recommended</li>
                <li>Other Chromium-based Android browsers (e.g. Samsung Internet, Edge) — may work</li>
            </ul>
            <p class="mb-0 small">
                Web NFC is not available on iOS (any browser) or on desktop browsers.
            </p>
        </div>

        <template v-else>
            <div class="d-flex gap-2 mb-3">
                <button v-if="!scanning" class="btn btn-primary" @click="startScan">Read NFC</button>
                <button v-else class="btn btn-outline-secondary" @click="stopScan">Stop</button>
                <button v-if="results.length" class="btn btn-outline-danger" @click="results = []">Clear</button>
            </div>

            <div v-if="status" class="alert alert-info">{{ status }}</div>
            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div v-for="(result, i) in results" :key="i" class="card mb-3">
                <div class="card-header small">
                    {{ result.time }} — serial: <code>{{ result.serialNumber || '(none)' }}</code>
                </div>
                <div class="card-body">
                    <p v-if="!result.records.length" class="mb-0 text-muted">
                        No NDEF records (tag is empty or not NDEF-formatted).
                    </p>
                    <div v-for="(record, j) in result.records" :key="j" class="mb-2">
                        <div class="small text-muted">
                            #{{ j + 1 }} type: {{ record.recordType }}
                            <span v-if="record.mediaType"> · media: {{ record.mediaType }}</span>
                            <span v-if="record.id"> · id: {{ record.id }}</span>
                            <span v-if="record.encoding"> · encoding: {{ record.encoding }}</span>
                            <span v-if="record.lang"> · lang: {{ record.lang }}</span>
                        </div>
                        <pre class="mb-0 text-break" style="white-space: pre-wrap">{{ record.data }}</pre>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
const TEXT_MEDIA = /^(text\/|application\/json)/;

function toHex(view) {
    const bytes = [];
    for (let i = 0; i < view.byteLength; i++) {
        bytes.push(view.getUint8(i).toString(16).padStart(2, '0'));
    }
    return bytes.join(' ');
}

function decodeRecord(record) {
    if (!record.data || record.data.byteLength === 0) return '(empty)';
    try {
        if (record.recordType === 'text') {
            return new TextDecoder(record.encoding || 'utf-8').decode(record.data);
        }
        if (['url', 'absolute-url'].includes(record.recordType)
            || (record.recordType === 'mime' && TEXT_MEDIA.test(record.mediaType || ''))) {
            return new TextDecoder().decode(record.data);
        }
    } catch {
        // fall through to hex dump
    }
    return toHex(record.data);
}

export default {
    data() {
        return {
            supported: 'NDEFReader' in window,
            secure: window.isSecureContext,
            scanning: false,
            status: '',
            error: '',
            results: [],
            abortController: null,
        };
    },
    beforeUnmount() {
        this.stopScan();
    },
    methods: {
        async startScan() {
            this.error = '';
            this.abortController = new AbortController();
            const reader = new window.NDEFReader();

            reader.onreading = ({ serialNumber, message }) => {
                this.results.unshift({
                    time: new Date().toLocaleTimeString(),
                    serialNumber,
                    records: message.records.map((record) => ({
                        recordType: record.recordType,
                        mediaType: record.mediaType,
                        id: record.id,
                        encoding: record.encoding,
                        lang: record.lang,
                        data: decodeRecord(record),
                    })),
                });
            };
            reader.onreadingerror = () => {
                this.error = 'Tag detected but could not be read (try another tag).';
            };

            try {
                await reader.scan({ signal: this.abortController.signal });
                this.scanning = true;
                this.status = 'Hold an NFC tag near the device…';
            } catch (err) {
                this.scanning = false;
                this.status = '';
                switch (err.name) {
                    case 'AbortError':
                        break;
                    case 'NotSupportedError':
                        this.error = 'This device has no NFC hardware, or the OS does not support NFC.';
                        break;
                    case 'NotReadableError':
                        this.error = 'NFC is unavailable — check that NFC is turned on in the phone settings.';
                        break;
                    case 'NotAllowedError':
                        this.error = 'NFC permission was denied.';
                        break;
                    default:
                        this.error = `${err.name}: ${err.message}`;
                }
            }
        },
        stopScan() {
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            this.scanning = false;
            this.status = '';
        },
    },
};
</script>
